import {ChevronDownIcon, ChevronRightIcon} from '@heroicons/react/solid';
import {useState} from 'react';

import type {
  AfriexProcessor,
  AfriexTransactionType,
  CurrencyMeta,
  ProcessorCapabilityStatus,
  ProcessorCapabilityStatusMeta,
  ProcessorCorridorMeta,
  ProcessorCurrencyMeta,
} from '@/types';
import * as formatDate from '@/utils/date';

import FeatureUpdateButton from './ProcessorUpdateButton';
import {ADMIN_FLAGS_NOTE, CAPABILITY_STATUS_DISPLAY} from './productionStatus';
import ProductionStatusBadge from './ProductionStatusBadge';

type ProcessorSummaryProps = {
  item: AfriexProcessor;
  isLoading?: boolean;
};

const ProcessorSummary = ({item}: ProcessorSummaryProps) => {
  const flags = item.meta?.flags;

  return (
    <div className="bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-5 py-5 sm:py-5">
      <div className="flex items-center gap-4 mb-3">
        <h2 className="font-semibold text-xl mr-3">{item.name}</h2>
      </div>

      <div>
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Processor ID
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {item?.id}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">Name</p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {item?.name}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">Code</p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {item?.code}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Production
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            <ProductionStatusBadge statuses={item.meta?.capabilityStatuses} />
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Admin flags
          </p>
          <div className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            <span className="inline-flex gap-2">
              <FlagBadge
                label="Active"
                value={flags?.isActive && !flags?.isDeactivated}
              />
              <FlagBadge label="Deactivated" value={flags?.isDeactivated} />
              <FlagBadge label="Down" value={flags?.isDown} />
              <FlagBadge label="Funded" value={flags?.isFunded} />
            </span>
            <p className="mt-1 text-xs font-normal whitespace-normal text-slate-500 dark:text-slate-400">
              {ADMIN_FLAGS_NOTE}
            </p>
          </div>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Date Created
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {formatDate.DDMonYYYY(item?.createdAt?.toString() || '')}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Date Updated
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {formatDate.DDMonYYYY(item?.updatedAt?.toString() || '')}
          </p>
        </div>
      </div>

      <CorridorSection
        corridors={item.meta?.corridors}
        statuses={item.meta?.capabilityStatuses}
      />

      <div className="text-sm text-slate-500 w-full pt-5 sm:pt-5">
        <div className="flex justify-end gap-4">
          <FeatureUpdateButton initialData={item} />
        </div>
      </div>
    </div>
  );
};

const FlagBadge = ({label, value}: {label: string; value?: boolean}) => (
  <span
    className={`px-2 py-0.5 rounded text-xs font-medium ${
      value
        ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
        : 'bg-gray-100 text-gray-500 dark:bg-gray-900 dark:text-gray-400'
    }`}
  >
    {label}
  </span>
);

type CorridorSectionProps = {
  corridors?: ProcessorCorridorMeta;
  statuses?: ProcessorCapabilityStatusMeta;
};

const CorridorSection = ({corridors, statuses}: CorridorSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  // `corridors` holds only what is enabled where the deploy ran, so a corridor
  // switched off in production is only in `statuses`. Show both.
  const corridorEntries = [
    ...new Set([
      ...Object.keys(corridors ?? {}),
      ...Object.keys(statuses ?? {}),
    ]),
  ].map(
    (currencyCode) =>
      [
        currencyCode,
        corridors?.[currencyCode as keyof ProcessorCorridorMeta],
      ] as const,
  );

  if (!corridorEntries.length) {
    return (
      <div className="mt-4">
        <h3 className="font-medium text-gray-700 dark:text-gray-200 mb-2">
          Corridors
        </h3>
        <p className="text-sm text-slate-400 italic">No corridors configured</p>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 font-medium text-gray-700 dark:text-gray-200 mb-2 hover:text-cyan-600"
      >
        {isOpen ? (
          <ChevronDownIcon className="w-4 h-4" />
        ) : (
          <ChevronRightIcon className="w-4 h-4" />
        )}
        <span>Corridors ({corridorEntries.length} currencies)</span>
      </button>

      {isOpen && (
        <div className="pl-6 space-y-3">
          {corridorEntries.map(([currencyCode, channelMap]) => (
            <CurrencyBlock
              key={currencyCode}
              currencyCode={currencyCode}
              channelMap={channelMap}
              statuses={statuses?.[currencyCode]}
            />
          ))}
        </div>
      )}
    </div>
  );
};

type ChannelStatuses = NonNullable<ProcessorCapabilityStatusMeta[string]>;

type CurrencyBlockProps = {
  currencyCode: string;
  channelMap?: ProcessorCurrencyMeta;
  statuses?: ChannelStatuses;
};

const CurrencyBlock = ({
  currencyCode,
  channelMap,
  statuses,
}: CurrencyBlockProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const channelEntries = [
    ...new Set([
      ...Object.keys(channelMap ?? {}),
      ...Object.keys(statuses ?? {}),
    ]),
  ].map(
    (channel) =>
      [channel, channelMap?.[channel as keyof ProcessorCurrencyMeta]] as const,
  );

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-md p-3">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-sm font-semibold text-cyan-700 dark:text-cyan-400 hover:text-cyan-500 w-full text-left"
      >
        {isOpen ? (
          <ChevronDownIcon className="w-4 h-4" />
        ) : (
          <ChevronRightIcon className="w-4 h-4" />
        )}
        <span>{currencyCode}</span>
        <span className="text-xs text-slate-400 font-normal">
          ({channelEntries.length} channel
          {channelEntries.length !== 1 ? 's' : ''})
        </span>
      </button>

      {isOpen && (
        <div className="mt-2 space-y-2 pl-6">
          {channelEntries.map(([channel, meta]) => (
            <ChannelDetail
              key={channel}
              channel={channel}
              meta={meta}
              statuses={statuses?.[channel as keyof ChannelStatuses]}
            />
          ))}
        </div>
      )}
    </div>
  );
};

type ChannelDetailProps = {
  channel: string;
  meta?: CurrencyMeta;
  statuses?: Partial<Record<AfriexTransactionType, ProcessorCapabilityStatus>>;
};

const ChannelDetail = ({channel, meta, statuses}: ChannelDetailProps) => {
  const statusEntries = Object.entries(statuses ?? {});
  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded p-2 text-xs">
      <p className="font-medium text-gray-700 dark:text-gray-200 mb-1">
        {formatChannelName(channel)}
      </p>

      {statusEntries.length ? (
        <div className="flex flex-wrap gap-1 mb-1">
          <span className="text-slate-400">Production:</span>
          {statusEntries.map(([type, status]) => (
            <span
              key={type}
              className={`px-1.5 py-0.5 rounded ${CAPABILITY_STATUS_DISPLAY[status].className}`}
            >
              {formatChannelName(type)}:{' '}
              {CAPABILITY_STATUS_DISPLAY[status].label}
            </span>
          ))}
        </div>
      ) : null}

      {meta?.supportedCountries?.length ? (
        <div className="flex flex-wrap gap-1 mb-1">
          <span className="text-slate-400">Countries:</span>
          {meta.supportedCountries.map((c) => (
            <span
              key={c}
              className="px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded"
            >
              {c}
            </span>
          ))}
        </div>
      ) : null}

      {meta?.transactionTypes?.length ? (
        <div className="flex flex-wrap gap-1 mb-1">
          <span className="text-slate-400">Types:</span>
          {meta.transactionTypes.map((t) => (
            <span
              key={t}
              className="px-1.5 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 rounded"
            >
              {t}
            </span>
          ))}
        </div>
      ) : null}

      {meta?.supportedInstitutions?.length ? (
        <div className="flex flex-wrap gap-1 mb-1">
          <span className="text-slate-400">Institutions:</span>
          {meta.supportedInstitutions.map((inst) => (
            <span
              key={inst}
              className="px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 rounded"
            >
              {inst}
            </span>
          ))}
        </div>
      ) : null}

      {meta?.supportsNameResolution !== undefined && (
        <p className="text-slate-400">
          Name Resolution:{' '}
          <span
            className={
              meta.supportsNameResolution ? 'text-green-600' : 'text-red-500'
            }
          >
            {meta.supportsNameResolution ? 'Yes' : 'No'}
          </span>
        </p>
      )}
    </div>
  );
};

const formatChannelName = (channel: string): string =>
  channel
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

export default ProcessorSummary;
